import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Play, Pause, Trash2, Plus, RefreshCw, Loader2,
  CheckCircle2, XCircle, Calendar, Zap, Globe, FileText, Link2, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { sitesAPI } from '@/lib/api';
import { formatDate, statusBadgeClass, statusLabel } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';



const JOB_ICONS = {
  refresh_entry_pages: Globe,
  refresh_bonus_pages: Zap,
  expand_pseo: FileText,
  internal_link_rebuild: Link2,
  sitemap_regen: Search,
  domain_health_check: Globe,
  generate_all_pages: Zap,
  multi_lang_generate: Globe,
};

export default function SchedulerPage() {
  const [jobs, setJobs] = useState([]);
  const [jobRuns, setJobRuns] = useState([]);
  const [sites, setSites] = useState([]);
  const [jobTypes, setJobTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newJob, setNewJob] = useState({ site_id: '', job_type: '', schedule: 'daily' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobsRes, runsRes, sitesRes, typesRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/job-runs'),
        api.get('/sites'),
        api.get('/job-types'),
      ]);
      setJobs(jobsRes.data);
      setJobRuns(runsRes.data);
      setSites(sitesRes.data);
      setJobTypes(typesRes.data.job_types || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    try {
      await api.post('/jobs', newJob);
      toast.success('Job olusturuldu');
      setShowCreate(false);
      setNewJob({ site_id: '', job_type: '', schedule: 'daily' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Olusturulamadi');
    }
  };

  const handleRunNow = async (jobId) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/run-now`);
      toast.success(data.message);
      setTimeout(loadData, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calistirilamadi');
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Bu job silinsin mi?')) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success('Job silindi');
      loadData();
    } catch (err) {
      toast.error('Silinemedi');
    }
  };

  const handleToggle = async (job) => {
    try {
      await api.put(`/jobs/${job.id}`, { enabled: !job.enabled });
      toast.success(job.enabled ? 'Job duraklatildi' : 'Job etkinlestirildi');
      loadData();
    } catch (err) {
      toast.error('Guncellenemedi');
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.brand_name || siteId?.slice(0, 8);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl shimmer" />)}</div>;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Icerik Zamanlayici</h1>
          <p className="text-sm text-muted-foreground mt-1">{jobs.length} aktif job</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="ghost" size="icon"><RefreshCw className="w-4 h-4" /></Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
                <Plus className="w-4 h-4" /> Yeni Job
              </Button>
            </DialogTrigger>
            <DialogContent className="z-[100]">
              <DialogHeader><DialogTitle>Yeni Zamanli Gorev</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <Label className="text-sm text-muted-foreground">Site</Label>
                  <Select value={newJob.site_id} onValueChange={(v) => setNewJob({...newJob, site_id: v})}>
                    <SelectTrigger className="mt-1.5 bg-secondary/40"><SelectValue placeholder="Site secin" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Gorev Tipi</Label>
                  <Select value={newJob.job_type} onValueChange={(v) => setNewJob({...newJob, job_type: v})}>
                    <SelectTrigger className="mt-1.5 bg-secondary/40"><SelectValue placeholder="Tip secin" /></SelectTrigger>
                    <SelectContent>{Object.entries(jobTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Zamanlama</Label>
                  <Select value={newJob.schedule} onValueChange={(v) => setNewJob({...newJob, schedule: v})}>
                    <SelectTrigger className="mt-1.5 bg-secondary/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Gunluk</SelectItem>
                      <SelectItem value="weekly">Haftalik</SelectItem>
                      <SelectItem value="manual">Manuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreate} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">Olustur</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      {/* Jobs List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {jobs.length === 0 ? (
          <Card className="border-border/70 col-span-2">
            <CardContent className="py-12 text-center">
              <Clock className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Henuz zamanli gorev yok</p>
            </CardContent>
          </Card>
        ) : jobs.map(job => {
          const Icon = JOB_ICONS[job.job_type] || Clock;
          const typeInfo = jobTypes[job.job_type] || {};
          return (
            <Card key={job.id} className="border-border/70 card-hover">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-secondary/60 flex items-center justify-center"><Icon className="w-4 h-4 text-muted-foreground" /></div>
                    <div>
                      <p className="text-sm font-medium">{typeInfo.label || job.job_type}</p>
                      <p className="text-xs text-muted-foreground">{getSiteName(job.site_id)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${job.enabled ? 'badge-aktif' : 'badge-duraklatildi'}`}>
                          {job.enabled ? 'Aktif' : 'Duraklatildi'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {job.schedule === 'daily' ? 'Gunluk' : job.schedule === 'weekly' ? 'Haftalik' : 'Manuel'}
                        </span>
                        {job.run_count > 0 && <span className="text-[10px] text-muted-foreground">{job.run_count}x calistirildi</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRunNow(job.id)}><Play className="w-3.5 h-3.5 text-emerald-400" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggle(job)}>{job.enabled ? <Pause className="w-3.5 h-3.5 text-amber-400" /> : <Play className="w-3.5 h-3.5" />}</Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(job.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Runs */}
      <Card className="border-border/70">
        <CardHeader><CardTitle className="text-base">Son Calistirmalar</CardTitle></CardHeader>
        <CardContent>
          {jobRuns.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henuz calistirma yapilmadi</p>
          ) : (
            <div className="space-y-2">
              {jobRuns.slice(0, 15).map(run => (
                <div key={run.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-3">
                    {run.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                     run.status === 'running' ? <Loader2 className="w-4 h-4 text-amber-400 animate-spin" /> :
                     run.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400" /> :
                     <Clock className="w-4 h-4 text-muted-foreground" />}
                    <div>
                      <p className="text-sm">{jobTypes[run.job_type]?.label || run.job_type}</p>
                      <p className="text-[10px] text-muted-foreground">{getSiteName(run.site_id)} - {formatDate(run.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusBadgeClass(run.status)}`}>{statusLabel(run.status)}</span>
                    {run.pages_generated > 0 && <p className="text-[10px] text-muted-foreground mt-1">{run.pages_generated} sayfa</p>}
                    {run.error_message && <p className="text-[10px] text-red-400 mt-1 max-w-[200px] truncate">{run.error_message}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
